import { useEffect, useState, type FormEvent } from "react";
import { SectionCard, StateBlock } from "../components/Cards";
import { createSection, deleteSection, getSections, getUser, updateOrSetSection } from "../lib/data";
import { setSeo } from "../lib/seo";
import { useAsync } from "../lib/useAsync";
import { useAuth } from "../lib/auth";
import type { Section } from "../lib/types";

export function Home() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const { data, loading, error } = useAsync(getSections, [refreshKey]);
  const { data: profile } = useAsync(() => (user ? getUser(user.uid) : Promise.resolve(null)), [user?.uid]);
  const isAdmin = profile?.role === "ADMIN";
  const refreshSections = () => setRefreshKey((key) => key + 1);

  useEffect(() => {
    setSeo({
      title: "My Local Forum | Home",
      description: "Browse local community sections and discover nearby discussions.",
      path: "/"
    });
  }, []);

  return (
    <section className="page">
      <h1>Home</h1>
      {isAdmin ? <SectionEditor onSaved={refreshSections} /> : null}
      <StateBlock loading={loading} error={error} empty={data?.length === 0} />
      <div className="masonry">
        {data?.map((section) => (
          <div className="card-stack" key={section.id}>
            <SectionCard section={section} />
            {isAdmin ? <SectionActions section={section} onChanged={refreshSections} /> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function SectionEditor({ section, onSaved }: { section?: Section; onSaved?: () => void }) {
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const input = {
      name: String(form.get("name") || "").trim(),
      description: String(form.get("description") || "").trim(),
      imageUrl: String(form.get("imageUrl") || "").trim() || null,
      appHtml: String(form.get("appHtml") || "").trim()
    };
    if (!input.name) {
      setNotice("Section name is required.");
      return;
    }

    setSaving(true);
    setNotice(null);
    try {
      if (section) await updateOrSetSection(section.id, input);
      else await createSection(input);
      if (!section) formElement.reset();
      setNotice(section ? "Section updated." : "Section created.");
      onSaved?.();
    } catch (err) {
      setNotice(err instanceof Error ? err.message : "Section could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="panel form-grid" onSubmit={submit}>
      <h2>{section ? "Edit Section" : "Add Section"}</h2>
      <input name="name" placeholder="Section name" defaultValue={section?.name || ""} />
      <textarea name="description" placeholder="Description" defaultValue={section?.description || ""} />
      <input name="imageUrl" placeholder="Image URL" defaultValue={section?.imageUrl || ""} />
      <textarea
        className="code-input"
        name="appHtml"
        placeholder="Optional full HTML app code, including <style> and <script>"
        defaultValue={section?.appHtml || ""}
        spellCheck={false}
      />
      <button className="primary-button" type="submit" disabled={saving}>
        {saving ? (section ? "Updating..." : "Creating...") : section ? "Update" : "Create"}
      </button>
      {notice ? <p className="form-notice">{notice}</p> : null}
    </form>
  );
}

function SectionActions({ section, onChanged }: { section: Section; onChanged: () => void }) {
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <details className="admin-actions">
      <summary>Edit</summary>
      <SectionEditor section={section} onSaved={onChanged} />
      <button
        className="danger-button"
        onClick={async () => {
          setNotice(null);
          try {
            await deleteSection(section.id);
            setNotice("Section deleted.");
            onChanged();
          } catch (err) {
            setNotice(err instanceof Error ? err.message : "Section could not be deleted.");
          }
        }}
      >
        Delete Section
      </button>
      {notice ? <p className="form-notice">{notice}</p> : null}
    </details>
  );
}
